// État des listes dans l'URL (apps/web/CLAUDE.md : filtres, tri et pagination partageables ; TRV-01).
// Correspond au contrat d'API : ?page=&per_page=&sort=-created_at&filter[x]=
// Analyse écrite à la main (sans zod) : ce module est chargé par le routeur, donc au démarrage (budget JS initial).

export const PER_PAGE_OPTIONS = [25, 50, 100] as const;
const DEFAULT_PER_PAGE = 25;

export interface ListSearch {
  page?: number;
  per_page?: number;
  sort?: string;
  filter?: Record<string, string>;
}

function positiveInt(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;
  return Number.isInteger(n) && n >= 1 ? n : undefined;
}

/** Paramètres de recherche lus dans l'URL, corrigés si invalides (per_page plafonné à 100, sinon 25). */
export function parseListSearch(raw: Record<string, unknown>): ListSearch {
  const search: ListSearch = {};
  const page = positiveInt(raw.page);
  if (raw.page !== undefined) search.page = page ?? 1;
  if (raw.per_page !== undefined) {
    const perPage = positiveInt(raw.per_page);
    search.per_page = perPage !== undefined && perPage <= 100 ? perPage : DEFAULT_PER_PAGE;
  }
  if (typeof raw.sort === 'string' && raw.sort.length <= 60 && /^-?[a-z_]+$/.test(raw.sort)) search.sort = raw.sort;
  if (raw.filter && typeof raw.filter === 'object' && !Array.isArray(raw.filter)) {
    // Le routeur interprète « filter[to_approve]=1 » ou « filter[compte]=521 » comme des nombres : remis en texte.
    const filter = Object.fromEntries(
      Object.entries(raw.filter as Record<string, unknown>)
        .filter(([, v]) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')
        .map(([k, v]) => [k, String(v)]),
    );
    if (Object.keys(filter).length > 0) search.filter = filter;
  }
  return search;
}

/** Paramètres de requête pour les hooks générés (`filter[type]=email`). */
export function toApiParams(search: ListSearch): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (search.page && search.page > 1) params.page = search.page;
  if (search.per_page) params.per_page = search.per_page;
  if (search.sort) params.sort = search.sort;
  for (const [key, value] of Object.entries(search.filter ?? {})) {
    if (value !== '') params[`filter[${key}]`] = value;
  }
  return params;
}

/** Chaîne de requête `?a=1&filter[b]=x` (URLSearchParams encode les crochets, ce que Laravel accepte). */
export function toQueryString(params: Record<string, string | number>): string {
  const entries = Object.entries(params);
  if (entries.length === 0) return '';
  return `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}`;
}

/** Tri TanStack Table → `sort=-created_at`. */
export function toSortParam(sorting: Array<{ id: string; desc: boolean }>): string | undefined {
  const first = sorting[0];
  return first ? `${first.desc ? '-' : ''}${first.id}` : undefined;
}

export function fromSortParam(sort: string | undefined): Array<{ id: string; desc: boolean }> {
  if (!sort) return [];
  return [{ id: sort.replace(/^-/, ''), desc: sort.startsWith('-') }];
}
